import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useRef, useState, useEffect } from 'react';
import './Testimonials.scss';
const TestimonialsTitle = ({ children }) => (_jsx("h2", { className: "title", children: children }));
const Testimonial = ({ children, imageUrl }) => {
    const review = React.Children.toArray(children).find(child => React.isValidElement(child) &&
        child.type === TestimonialReview);
    const endorser = React.Children.toArray(children).find(child => React.isValidElement(child) &&
        child.type === TestimonialEndorser);
    const img = imageUrl ? _jsx("img", { src: imageUrl }) : _jsx(_Fragment, {});
    return (_jsxs("article", { className: "testimonial", children: [_jsxs("header", { children: [img, _jsx("svg", { className: "quote-icon", viewBox: "0 0 57 48", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M0 36.285c8.72-1.14 11.626-5.532 11.949-11.553H.646V0h26.32v18.224C26.966 40.515 15.986 46.536 0 48V36.285zm29.872 0c8.882-1.14 11.788-5.532 11.95-11.553H30.517V0H57v18.224C57 40.515 45.858 46.536 29.872 48V36.285z" }) })] }), _jsxs("blockquote", { children: [review, endorser] })] }));
};
const TestimonialEndorser = ({ children }) => {
    const endorserName = React.Children.toArray(children).find(child => React.isValidElement(child) &&
        child.type === TestimonialEndorserName);
    const endorserTitle = React.Children.toArray(children).find(child => React.isValidElement(child) &&
        child.type === TestimonialEndorserTitle);
    const endorserWorkplace = React.Children.toArray(children).find(child => React.isValidElement(child) &&
        child.type === TestimonialEndorserWorkplace);
    return (_jsx("footer", { children: _jsxs("cite", { children: [_jsx("span", { className: "endorser-name", children: endorserName }), _jsx("span", { className: "endorser-title", children: endorserTitle }), _jsx("span", { className: "endorser-workplace", children: endorserWorkplace })] }) }));
};
const TestimonialEndorserName = ({ children }) => (_jsx(_Fragment, { children: children }));
const TestimonialEndorserTitle = ({ children }) => (_jsx(_Fragment, { children: children }));
const TestimonialEndorserWorkplace = ({ children }) => (_jsx(_Fragment, { children: children }));
const TestimonialReview = ({ children }) => (_jsx("p", { children: children }));
export const Testimonials = ({ children, className, carouselInterval = 5 }) => {
    const carouselRef = useRef(null);
    const indexRef = useRef(0);
    const intervalIdRef = useRef(null);
    const userInteractionRef = useRef(false);
    const pagesPerViewRef = useRef(0);
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    useEffect(() => { indexRef.current = currentTestimonial; });
    useEffect(() => {
        if (carouselRef.current) {
            let ticking = false;
            const pw = carouselRef.current.clientWidth;
            const cw = carouselRef.current.children[0].clientWidth;
            pagesPerViewRef.current = Math.round(pw / cw);
            const handleScroll = () => {
                if (ticking)
                    return;
                ticking = true;
                window.requestAnimationFrame(() => {
                    if (userInteractionRef.current && intervalIdRef.current !== null) {
                        clearInterval(intervalIdRef.current);
                        intervalIdRef.current = null;
                        userInteractionRef.current = false;
                    }
                    const el = carouselRef.current;
                    if (el) {
                        const idx = Math.round(el.scrollLeft / el.children[0].clientWidth);
                        setCurrentTestimonial(idx);
                        indexRef.current = idx;
                        ticking = false;
                    }
                });
            };
            const onUserInteraction = () => {
                userInteractionRef.current = true;
            };
            carouselRef.current.addEventListener('scroll', handleScroll, { passive: true });
            carouselRef.current.addEventListener('pointerdown', onUserInteraction, { passive: true, capture: true });
            carouselRef.current.addEventListener('touchstart', onUserInteraction, { passive: true, capture: true });
            carouselRef.current.addEventListener('keydown', onUserInteraction, { passive: true, capture: true });
            handleScroll();
            return () => {
                if (carouselRef.current) {
                    const carouselElement = carouselRef.current;
                    carouselElement.removeEventListener('scroll', handleScroll);
                    carouselElement.removeEventListener('pointerdown', onUserInteraction);
                    carouselElement.removeEventListener('touchstart', onUserInteraction);
                    carouselElement.removeEventListener('keydown', onUserInteraction);
                }
                if (intervalIdRef.current !== null) {
                    clearInterval(intervalIdRef.current);
                    intervalIdRef.current = null;
                }
            };
        }
    }, []);
    const title = React.Children.toArray(children).find(child => React.isValidElement(child) &&
        child.type === TestimonialsTitle);
    const reviews = React.Children.toArray(children).filter(child => React.isValidElement(child) &&
        child.type === Testimonial);
    useEffect(() => {
        const count = reviews.length;
        if (!count) {
            return;
        }
        intervalIdRef.current = setInterval(() => {
            if (!carouselRef.current) {
                return;
            }
            const cw = carouselRef.current.children[0].clientWidth;
            const next = (indexRef.current + 1) % (count - (pagesPerViewRef.current - 1));
            const opts = {
                left: next * cw,
                behavior: 'smooth'
            };
            carouselRef.current.scrollTo(opts);
        }, carouselInterval * 1000);
    }, [reviews.length]);
    const onClickDots = (e) => {
        const svg = e.currentTarget;
        const cir = e.target.closest('circle');
        if (!cir || !carouselRef.current) {
            return;
        }
        userInteractionRef.current = true;
        const idx = Array.prototype.indexOf.call(svg.children, cir);
        if (idx >= 0) {
            const cw = carouselRef.current.children[0].clientWidth;
            const opts = {
                left: idx * cw,
                behavior: 'smooth'
            };
            carouselRef.current.scrollTo(opts);
        }
    };
    const ppw = pagesPerViewRef.current;
    const dots = (_jsx("svg", { viewBox: `0 0 ${reviews.length * 4} 2`, role: "tablist", "aria-label": "carousel-pagination", onClick: onClickDots, children: reviews.map((_, i) => _jsx("circle", { className: (i >= currentTestimonial && i < currentTestimonial + ppw) ? 'active' : '', cx: i * 4 + 1, cy: 1, r: 1 }, i)) }));
    return (_jsx(_Fragment, { children: _jsxs("section", { className: `testimonials ${className !== null && className !== void 0 ? className : ''}`, children: [_jsx("div", { className: "page-dim", children: title }), _jsx("div", { className: "page-dim carousel", ref: carouselRef, children: reviews }), _jsx("div", { className: "dots", children: dots })] }) }));
};
TestimonialEndorser.Name = TestimonialEndorserName;
TestimonialEndorser.Title = TestimonialEndorserTitle;
TestimonialEndorser.Workplace = TestimonialEndorserWorkplace;
Testimonial.Endorser = TestimonialEndorser;
Testimonial.Review = TestimonialReview;
Testimonials.Title = TestimonialsTitle;
Testimonials.Testimonial = Testimonial;
